import React, { useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { companyOf } from '../utils/company';
import { CheckCircle, CreditCard, AlertCircle, Star, Loader } from 'lucide-react';

import { API_BASE_URL, publicApi } from '../services/api';
import { money, fullDate, tinyDate } from '../utils/format';
import LawnWalkthroughButton from '../components/LawnWalkthroughButton';
import {
  PublicPage, PublicLoading, DeadLink, Panel, H2, ButtonPrimary, StickyBar,
} from '../components/public';

/**
 * The invoice a customer opens from a text.
 *
 * Rebuilt Sep 19 2026. The old page put the amount owed in a table footer and
 * the Pay button below a tip grid, a review offer and a status banner — three
 * things to scroll past before the one thing they came to do. Now the amount
 * is the first thing on the page, Pay is pinned to the bottom of the screen,
 * the tip sits directly above it, and the review offer waits at the end.
 *
 * Same Stripe checkout call, same tip percentages, same review-claim upload.
 */

export default function InvoicePublic() {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const paymentReturned = new URLSearchParams(location.search).get('payment') === 'success';

  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [tipPct, setTipPct] = useState<number>(0);   // 0 · 10 · 15 · 25 — a tip rides on the card payment (owner, Sep 18 2026)
  // Several open bills → one payment for all of them by default; "just this one" is a text link (owner, Oct 26 2026).
  const [payAllMode, setPayAllMode] = useState<boolean>(true);

  const { data, isLoading, isError } = useQuery(
    ['public-invoice', token],
    () => publicApi.get(`/invoices/public/${token}`).then(r => r.data),
    {
      enabled: !!token,
      retry: false,
      refetchInterval: paymentReturned ? 4000 : false,
      refetchOnWindowFocus: true,
    }
  );
  const company = companyOf(data);

  if (isLoading) return <PublicLoading />;
  if (isError || !data?.invoice) return <DeadLink what="invoice" company={company} />;

  const { invoice, lineItems = [], stripeEnabled = false, stripeMode = 'not_configured' } = data;
  const payAll: { count: number; amount: number; words: string; bills: { invoice_number: string; balance: number; period: string | null; due_date: string | null; this_one: boolean }[]; tips: { options: { pct: number; amount: number }[] } | null } | null = data?.pay_all || null;
  const payingAll = !!payAll && payAllMode;
  const tipOptions: { pct: number; amount: number }[] = (payingAll ? payAll?.tips?.options : data?.tips?.options) || [];
  const tipAmount = tipOptions.find(o => o.pct === tipPct)?.amount || 0;
  const payTotal = (payingAll ? Number(payAll!.amount) : Number(invoice.total)) + tipAmount;
  const paidTip = Number(invoice.tip_amount) || 0;
  const isPaid = invoice.status === 'paid';
  const isVoid = invoice.status === 'void';
  const isTestMode = stripeMode === 'test';
  // The server says whether this invoice is payable. A draft (internal Path-B
  // bookkeeping) is not — no Pay button, no review-discount offer.
  const payable = data?.payable === true;

  async function startCheckout() {
    setPaying(true);
    setPayError(null);
    try {
      const res = await publicApi.post(`/invoices/public/${token}/${payingAll ? 'create-checkout-session-all' : 'create-checkout-session'}`, { tip_pct: tipPct });
      window.location.href = res.data.url;
    } catch (err: any) {
      setPayError(err.response?.data?.message || 'Could not start payment. Please try again.');
      setPaying(false);
    }
  }

  return (
    <PublicPage company={company} context={invoice.invoice_number}>
      {isTestMode && (
        <div className="-mx-4 sm:-mx-6 -mt-5 mb-1 bg-amber-500 text-white text-center text-[13px] font-black uppercase tracking-wide py-2 px-4">
          Test mode. No real money will be charged.
        </div>
      )}

      {/* What you owe, first thing. */}
      <section className="bg-white rounded-3xl border border-brand-100 shadow-lift overflow-hidden">
        <div className="px-5 pt-5">
          <p className="text-[13px] font-black uppercase tracking-wide text-brand-600">
            {isPaid ? 'Paid in full' : isVoid ? 'Cancelled' : payable ? 'Amount due' : 'Your invoice'}
          </p>
          <p className="mt-1 text-5xl font-black tracking-tight text-brand-600 tabular-nums leading-none">{money(invoice.total)}</p>
          <p className="text-[15px] text-gray-700 mt-2">
            For {invoice.customer_name}
            {invoice.billing_period_start ? <>, {tinyDate(invoice.billing_period_start)} to {tinyDate(invoice.billing_period_end)}</> : null}
          </p>
          {invoice.due_date && !isPaid && !isVoid && (
            <p className="text-[15px] font-semibold text-gray-900 mt-1">Due by {fullDate(invoice.due_date)}</p>
          )}
          {/* A commercial account's bill carries who it is addressed to, the PO and the terms. */}
          {data?.bill_to && (
            <div className="mt-3 rounded-2xl border border-brand-100 bg-brand-50/60 px-3.5 py-2.5 text-[14px] text-gray-800 space-y-0.5">
              {data.bill_to.company && <p><span className="font-bold text-gray-900">Bill to</span> {data.bill_to.company}</p>}
              {data.bill_to.attention && <p>Attn: {data.bill_to.attention}</p>}
              {data.bill_to.address && <p className="whitespace-pre-line">{data.bill_to.address}</p>}
              {data.bill_to.po_number && <p>PO {data.bill_to.po_number}</p>}
              {data.bill_to.terms && <p>Terms: {data.bill_to.terms === 'Net 0' ? 'Due on receipt' : data.bill_to.terms}</p>}
            </div>
          )}
        </div>
        <div className="px-5 py-4 mt-5 bg-brand-50 border-t border-brand-100">
          <p className="text-[14px] font-semibold text-brand-800">{invoice.invoice_number}</p>
        </div>
      </section>

      {isPaid && (
        <Panel tone="good">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-8 w-8 text-emerald-600 flex-shrink-0" aria-hidden />
            <div>
              <H2>Paid{paidTip > 0 ? `, and thank you for the ${money(paidTip)} tip` : ''}</H2>
              {invoice.paid_at && (
                <p className="text-[15px] text-gray-800 mt-1">
                  {fullDate(invoice.paid_at)}{paidTip > 0 ? ` · ${money(Number(invoice.total) + paidTip)} in all` : ''}
                </p>
              )}
            </div>
          </div>
        </Panel>
      )}

      {isVoid && (
        <Panel>
          <H2>This invoice was cancelled</H2>
          <p className="text-[15px] text-gray-800 mt-2">Nothing is owed on it. Text us if that looks wrong.</p>
        </Panel>
      )}

      {paymentReturned && !isPaid && (
        <Panel tone="brand">
          <div className="flex items-start gap-3">
            <Loader className="h-6 w-6 text-brand-700 animate-spin flex-shrink-0 mt-0.5" aria-hidden />
            <div>
              <H2>Payment submitted</H2>
              <p className="text-[15px] text-gray-800 mt-1">We are confirming it with Stripe. This page updates on its own.</p>
            </div>
          </div>
        </Panel>
      )}

      {lineItems.length > 0 && (
        <Panel title="What you're paying for">
          {/* Each line says the day the work was done; the server lists them in day order, month lines after. */}
          <ul className="divide-y divide-gray-200">
            {lineItems.map((li: any) => (
              <li key={li.id} className="py-3 first:pt-0 last:pb-0 flex items-start gap-3">
                <span className="w-12 flex-shrink-0 text-[13px] font-semibold text-gray-600 tabular-nums pt-0.5">
                  {li.service_date ? tinyDate(li.service_date) : ''}
                </span>
                <span className="text-[15px] text-gray-900 flex-1 min-w-0">
                  {li.description}
                  {li.quantity != null && li.quantity !== 1 && li.unit_price != null && (
                    <span className="block text-[13px] text-gray-600 tabular-nums">{li.quantity} × {money(li.unit_price)}</span>
                  )}
                </span>
                <span className={`text-[15px] font-bold tabular-nums whitespace-nowrap ${li.fee_kind ? 'text-amber-800' : 'text-gray-900'}`}>{money(li.amount)}</span>
              </li>
            ))}
          </ul>
          {Number(invoice.discount_amount) > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5">
              <div className="flex items-center justify-between text-[15px] text-gray-800">
                <span>Services</span><span className="tabular-nums">{money(invoice.subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-[15px] font-bold text-emerald-700">
                <span>{invoice.discount_label || 'Discount'}</span><span className="tabular-nums">&minus;{money(invoice.discount_amount)}</span>
              </div>
            </div>
          )}
          <div className="mt-4 pt-4 border-t-2 border-brand-100 flex items-center justify-between">
            <span className="text-[15px] font-black uppercase tracking-tight text-gray-900">Total</span>
            <span className="text-2xl font-black tracking-tight text-brand-600 tabular-nums">{money(invoice.total)}</span>
          </div>
          {/* The terms on every bill (owner, Nov 1 2026; the same words sit on the service agreement). */}
          <p className="mt-3 text-[12px] leading-snug text-gray-500">{data?.late_fee_terms || 'Payment is due on receipt. Invoices more than 10 days past due get a $25 late fee. Balances more than 30 days past due accrue 1.5% interest per month, and service may be paused until the account is paid.'}</p>
          {isPaid && paidTip > 0 && (
            <div className="mt-2 flex items-center justify-between text-[15px] text-gray-800">
              <span>Tip{invoice.tip_pct ? ` (${invoice.tip_pct}%)` : ''}</span><span className="tabular-nums">{money(paidTip)}</span>
            </div>
          )}
        </Panel>
      )}

      {payable && !stripeEnabled && (
        <Panel>
          <p className="text-[15px] text-gray-800">
            Card payments are not switched on right now. Text us and we will take a check, cash, Venmo or Zelle.
          </p>
        </Panel>
      )}

      {payable && data?.review_discount_applied && (
        <Panel tone="good">
          <p className="text-[15px] font-bold text-emerald-900">$10 off applied for your Google review. Thank you.</p>
        </Panel>
      )}

      {/* Everything they owe, in one go: their other open bills and one payment for all of them (owner, Oct 26 2026). */}
      {payable && payAll && (
        <Panel>
          <H2>{payAll.count} open bills</H2>
          <p className="text-[15px] text-gray-700 mt-1">You have {payAll.words}. Pay them together below, or just this one.</p>
          <ul className="mt-3 divide-y divide-gray-100">
            {payAll.bills.map(b => (
              <li key={b.invoice_number} className="py-2 flex items-center justify-between gap-3 text-[15px]">
                <span className={b.this_one ? 'font-bold text-gray-900' : 'text-gray-800'}>{b.invoice_number}{b.period ? ` · ${b.period}` : ''}{b.this_one ? ' (this bill)' : ''}</span>
                <span className="tabular-nums font-semibold text-gray-900">{money(b.balance)}</span>
              </li>
            ))}
            <li className="py-2 flex items-center justify-between gap-3 text-[15px] font-black text-gray-900"><span>All together</span><span className="tabular-nums">{money(payAll.amount)}</span></li>
          </ul>
          <button type="button" onClick={() => { setPayAllMode(m => !m); setTipPct(0); }} disabled={paying || paymentReturned}
            className="mt-3 text-[15px] font-semibold text-brand-700 underline">
            {payAllMode ? `Pay just this bill (${money(Number(invoice.total))}) instead` : `Pay all ${payAll.count} bills (${money(payAll.amount)}) instead`}
          </button>
        </Panel>
      )}

      {payable && data?.review_discount_available && <ReviewDiscountCard token={token!} />}

      {/* The free lawn walkthrough (owner, Oct 20 2026) — outlined, never above Pay; not on a business's bill. */}
      {!data?.bill_to?.company && <LawnWalkthroughButton kind="invoice" token={token!} />}

      {payError && (
        <Panel>
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" aria-hidden />
            <p className="text-[15px] font-semibold text-red-700">{payError}</p>
          </div>
        </Panel>
      )}

      {payable && stripeEnabled && (
        <StickyBar
          sub={isTestMode ? 'Test mode. Card 4242 4242 4242 4242. No real charge.' : 'Secured by Stripe. You confirm the amount before anything is charged.'}
        >
          {tipOptions.length > 0 && (
            <div className="mb-3">
              <p className="text-[14px] font-semibold text-gray-800 mb-1.5">
                Want to add a tip? <span className="font-normal text-gray-600">Goes on the same payment.</span>
              </p>
              <div className="grid grid-cols-4 gap-1.5">
                {[{ pct: 0, amount: 0 }, ...tipOptions].map(o => {
                  const on = tipPct === o.pct;
                  return (
                    <button key={o.pct} type="button" onClick={() => setTipPct(o.pct)} disabled={paying || paymentReturned}
                      aria-pressed={on}
                      className={`press rounded-2xl border-2 px-1 min-h-[56px] ${on ? 'bg-brand-600 border-brand-600 text-black shadow-brand' : 'bg-white border-brand-200 text-gray-900 shadow-soft'}`}>
                      <span className="block text-[15px] font-black">{o.pct ? `${o.pct}%` : 'None'}</span>
                      {o.pct ? <span className={`block text-[12px] tabular-nums ${on ? 'text-white/85' : 'text-gray-600'}`}>{money(o.amount)}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <ButtonPrimary onClick={startCheckout} disabled={paying || paymentReturned}>
            <CreditCard className="h-5 w-5" aria-hidden />
            {paying ? 'Starting payment' : payingAll ? `Pay all ${payAll!.count} bills · ${money(payTotal)}` : `Pay ${money(payTotal)} by card`}
          </ButtonPrimary>
        </StickyBar>
      )}
    </PublicPage>
  );
}

/**
 * "$10 off for a Google review" — the customer taps the link, leaves the
 * review, screenshots it and uploads it here. The $10 comes off at once (owner,
 * Oct 6 2026: no approval first — he looks at it later), once per customer; the
 * server's reply says what happened.
 */
function ReviewDiscountCard({ token }: { token: string }) {
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [msg, setMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const REVIEW_URL = 'https://g.page/r/CWkj4oAnq4kUEAI/review';

  async function send(file: File) {
    setState('sending');
    try {
      const fd = new FormData();
      fd.append('screenshot', file);
      const res = await fetch(`${API_BASE_URL}/review-claims/public/${token}`, { method: 'POST', body: fd });
      const body = await res.json().catch(() => ({}));
      setMsg(body.message || (res.ok ? 'Thanks!' : 'Could not upload that. Please text it to us instead.'));
      setState(res.ok ? 'done' : 'idle');
    } catch {
      setMsg('Could not upload that. Please text it to us instead.');
      setState('idle');
    }
  }

  return (
    <Panel tone="brand">
      <div className="flex items-start gap-3">
        <Star className="h-6 w-6 text-brand-700 flex-shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          <H2>Want $10 off this invoice?</H2>
          <p className="text-[15px] text-gray-900 mt-2">
            Leave us a quick Google review, screenshot it, and upload it here. We take $10 off before you pay.
          </p>
          <div className="mt-4 space-y-2">
            <ButtonPrimary href={REVIEW_URL} aria-label="Write a Google review">Write the review</ButtonPrimary>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={state !== 'idle'}
              className="press w-full min-h-[54px] rounded-2xl border-2 border-brand-200 bg-white text-brand-800 text-[15px] font-black uppercase tracking-wide shadow-soft disabled:opacity-50">
              {state === 'sending' ? 'Uploading' : state === 'done' ? 'Sent' : 'Upload my screenshot'}
            </button>
            <input ref={inputRef} type="file" accept="image/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) send(f); e.target.value = ''; }} />
          </div>
          {msg && <p className="text-[15px] font-semibold text-gray-900 mt-3">{msg}</p>}
        </div>
      </div>
    </Panel>
  );
}
